import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Play, Instagram, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { getImageUrl } from '../../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Word-by-word blur-to-focus title
const TitleReveal = ({ text, className }) => {
  const words = text.split(' ');
  return (
    <motion.h2
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-80px' }}
      variants={{
        visible: { transition: { staggerChildren: 0.12 } },
      }}
    >
      {words.map((word, i) => (
        <motion.span
          key={`${word}-${i}`}
          className="inline-block mr-[0.25em]"
          variants={{
            hidden: { opacity: 0, y: 18, filter: 'blur(10px)' },
            visible: {
              opacity: 1,
              y: 0,
              filter: 'blur(0px)',
              transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
            },
          }}
        >
          {word}
        </motion.span>
      ))}
    </motion.h2>
  );
};

// Detect aftermovie / teaser from title
const getVideoBadge = (title = '') => {
  const lower = title.toLowerCase();
  if (lower.includes('aftermovie')) return 'Aftermovie';
  if (lower.includes('teaser')) return 'Teaser';
  if (lower.includes('recap')) return 'Recap';
  return null;
};

const VideoCard = ({ video, index }) => {
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: '-60px' });
  const badgeLabel = getVideoBadge(video.title);

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 40, rotate: -2 }}
      animate={isInView ? { opacity: 1, y: 0, rotate: 0 } : {}}
      transition={{ delay: index * 0.12, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      style={{ willChange: 'transform' }}
    >
      <a
        href={video.instagram_url || video.youtube_url || '#'}
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full group relative aspect-video rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-[#C9A84C]/50 transition-colors duration-300"
        data-testid={`video-card-${video.id}`}
        aria-label={`${video.title} — ouvrir sur ${video.instagram_url ? 'Instagram' : 'YouTube'}`}
      >
            {/* Thumbnail */}
            <img
              src={getImageUrl(video.thumbnail)}
              alt={video.title}
              className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
              loading="lazy"
            />

            {/* Dark overlay - animated */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30 group-hover:from-black/85 group-hover:via-black/30 group-hover:to-transparent transition-all duration-500" />

            {/* Animated badge */}
            {badgeLabel && (
              <div className="absolute top-3 left-3 z-10">
                <motion.span
                  className="inline-block px-3 py-1 rounded-full text-[10px] font-display uppercase tracking-widest text-[#060606]"
                  style={{
                    backgroundImage:
                      'linear-gradient(120deg, #C9A84C 0%, #E2C56A 35%, #F3D97A 50%, #E2C56A 65%, #C9A84C 100%)',
                    backgroundSize: '200% 100%',
                  }}
                  animate={{ backgroundPosition: ['0% 50%', '200% 50%'] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                >
                  {badgeLabel}
                </motion.span>
              </div>
            )}

            {/* Play Button with pulsing gold rings */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative flex items-center justify-center">
                {/* Pulsing ring 1 */}
                <motion.span
                  aria-hidden="true"
                  className="absolute inset-0 rounded-full border border-[#C9A84C]"
                  style={{ width: 56, height: 56 }}
                  animate={{ scale: [1, 1.7], opacity: [0.7, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
                />
                {/* Pulsing ring 2 */}
                <motion.span
                  aria-hidden="true"
                  className="absolute inset-0 rounded-full border border-[#C9A84C]"
                  style={{ width: 56, height: 56 }}
                  animate={{ scale: [1, 1.7], opacity: [0.5, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeOut', delay: 1 }}
                />
                {/* Play button core */}
                <motion.div
                  className="relative w-14 h-14 rounded-full bg-[#C9A84C] flex items-center justify-center shadow-[0_0_30px_rgba(201,168,76,0.45)]"
                  whileHover={{ scale: 1.12 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 18 }}
                >
                  <Play className="w-6 h-6 text-[#060606] ml-1" fill="currentColor" />
                </motion.div>
              </div>
            </div>

            {/* Instagram pill (top-right) */}
            {video.instagram_url && (
              <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 text-white text-xs z-10">
                <Instagram className="w-3.5 h-3.5" />
                <ExternalLink className="w-3 h-3 opacity-70" />
              </div>
            )}

            {/* Title */}
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
              <h3 className="font-display text-sm text-[#F5F0E8] text-left transform transition-transform duration-500 group-hover:translate-y-[-2px]">
                {video.title}
              </h3>
            </div>
      </a>
    </motion.div>
  );
};

const VideosSection = ({ limit = 3 }) => {
  const { t } = useTranslation();
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/videos`);
        setVideos(response.data.videos.slice(0, limit));
      } catch (error) {
        console.error('Error fetching videos:', error);
      }
    };
    fetchVideos();
  }, [limit]);

  return (
    <section
      className="relative py-16 sm:py-24 bg-[#060606] overflow-hidden"
      data-testid="videos-section"
    >
      {/* Animated background gradient */}
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-70"
        animate={{
          background: [
            'radial-gradient(circle at 15% 80%, rgba(201,168,76,0.10), transparent 50%)',
            'radial-gradient(circle at 85% 20%, rgba(201,168,76,0.10), transparent 50%)',
            'radial-gradient(circle at 15% 80%, rgba(201,168,76,0.10), transparent 50%)',
          ],
        }}
        transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <TitleReveal
            text={t('videos.title')}
            className="font-display text-4xl sm:text-5xl tracking-tight text-[#F5F0E8] mb-3"
          />
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ delay: 0.5, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif italic text-lg text-[#B9B2A8]"
          >
            {t('videos.subtitle')}
          </motion.p>
        </div>

        {/* Videos Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.map((video, index) => (
            <VideoCard key={video.id} video={video} index={index} />
          ))}
        </div>

        {/* CTA */}
        <motion.div
          className="text-center mt-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ delay: 0.2, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <Link to="/videos">
            <Button
              variant="outline"
              className="border-[#C9A84C]/30 text-[#C9A84C] hover:bg-[#C9A84C]/10 hover:border-[#C9A84C] font-display uppercase tracking-wider transition-all duration-300"
              data-testid="videos-see-all-button"
            >
              Voir toutes les vidéos →
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default VideosSection;
